import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-A3 — quiet "check this entry" list. Physically-impossible fuel readings
// (e.g. +970% consumption) are wrong entries, not real events. They are already
// excluded from averages and estimates; this just lists them to fix calmly.

interface Row { id: string; plate: string; date: string | null; liters: number; totalSar: number; lPer100km: number; reason: string }

export default function DataQualityPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['data-quality'],
    queryFn: async () => (await apiClient.get<{ items: Row[]; count: number }>('/api/v1/project-reports/data-quality')).data,
  });
  const rows = data?.items ?? [];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Data quality · check this entry</div>
          <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard/settings'; }} className="gap-2"><ArrowLeft className="h-4 w-4" /> Settings</Button>
        </div>
      </header>
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8">
        <div className="rounded-xl border border-border bg-card p-4 mb-6 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            These fuel readings are physically impossible, so they are almost certainly a wrong entry, not a real event.
            They are <b>already excluded</b> from fleet averages and the cost estimates, so they are not breaking any numbers.
            Fix or ignore them calmly — no alarm.
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-start font-medium px-4 py-2">Truck</th>
                <th className="text-start font-medium px-4 py-2">Date</th>
                <th className="text-end font-medium px-4 py-2">Liters</th>
                <th className="text-end font-medium px-4 py-2">SAR</th>
                <th className="text-end font-medium px-4 py-2">L/100km</th>
                <th className="text-start font-medium px-4 py-2">Why flagged</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!isLoading && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No suspicious entries. Fuel data looks clean.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-2 font-medium">{r.plate}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.date ? String(r.date).slice(0, 10) : '—'}</td>
                  <td className="px-4 py-2 text-end">{r.liters.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                  <td className="px-4 py-2 text-end">{r.totalSar.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="px-4 py-2 text-end text-amber-600">{r.lPer100km}</td>
                  <td className="px-4 py-2 text-muted-foreground">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
